<template>
	<div class="container_section p-5">
		<!-- Header: título + slot para acciones extra (Comparación, Ver Barra...)
			+ selector de período. El selector se oculta cuando filterPeriod es
			YTD/year porque el rango ya es implícito. -->
		<div class="table_header is-flex is-justify-content-space-between is-align-items-center mb-4">
			<!-- slot title sobreescribe la prop simple cuando el consumidor
				necesita componer ícono + texto (Rentabilidad). El fallback
				conserva el comportamiento original de ResultadosTab. -->
			<div class="is-flex is-align-items-center">
				<slot name="title">
					<span class="tittle_section">{{ title }}</span>
				</slot>
			</div>
			<div class="table_actions is-flex is-align-items-center">
				<slot name="header-actions"></slot>
				<div v-if="showPeriodSelect" style="min-width: 165px">
					<SelectComponent
						:value="selectedPeriodRange"
						@input="(val) => $emit('update-period', val)"
						theme="report_theme"
						:options="periodOptions" />
				</div>
			</div>
		</div>

		<section>
			<div class="table_container" ref="tableScroll" v-if="!isLoading">
				<table class="custom_table">
					<colgroup>
						<col style="width: 200px;">
						<col style="width: 160px;" v-for="header in headers" :key="header">
					</colgroup>
					<thead>
						<tr>
							<th scope="col" class="sticky-column header_label nobreak"></th>
							<th scope="col" class="has-text-right nobreak is-capitalized"
								v-for="header in headers" :key="header">
								{{ header }}
							</th>
						</tr>
					</thead>
					<tbody>
						<tr v-for="row in rows" :key="row.id">
							<th scope="row" class="sticky-column nobreak" :class="row.theme || 'theme_row_white'">
								<span>{{ row.label }}</span>
								<!-- chevron data-driven: cualquier fila con hasChilds emite
									el toggle. El padre maneja el estado expandido. -->
								<span v-if="row.hasChilds"
									class="is-pulled-right actual_button_arrow_down"
									@click="$emit('toggle-childs', row.id)">
									<ArrowDown class="color_arrow" :class="{ rotate_arrow: expandedRows.includes(row.id) }" />
								</span>
							</th>
							<td v-for="header in headers" :key="header" class="has-text-right nobreak"
								:class="row.theme || 'theme_row_white'">
								<template v-if="row.isMoney">
									{{ money(row.itemsDict[header] || 0) }}
								</template>
								<template v-else>
									{{ Math.round(row.itemsDict[header] || 0) }}%
								</template>
							</td>
						</tr>
					</tbody>
				</table>
			</div>
			<div v-else class="loading_state py-6 has-text-centered">
				<p>Cargando datos...</p>
			</div>
		</section>
	</div>
</template>

<script>
import { parseToMoney } from "@/utils/currency";
import SelectComponent from "@/components/classification/SelectComponent";
import ArrowDown from "@/components/svg/ArrowDown.vue";

export default {
	name: "FinancialResultsTable",
	components: { SelectComponent, ArrowDown },
	props: {
		title: { type: String, default: "" },
		rows: { type: Array, required: true },
		headers: { type: Array, required: true },
		isLoading: { type: Boolean, default: false },
		selectedPeriodRange: { type: String, default: "" },
		periodOptions: { type: Array, default: () => [] },
		filterPeriod: { type: String, default: "month" },
		expandedRows: { type: Array, default: () => [] },
	},
	computed: {
		// YTD y year ya implican un rango fijo (año en curso, año cerrado);
		// mostrar un selector adicional confunde. Mismo criterio que la
		// versión inline en ResultadosTab.
		showPeriodSelect() {
			return !["ytd", "year"].includes(this.filterPeriod);
		},
	},
	methods: {
		money(val) { return parseToMoney(val); },
		scrollToRight() {
			this.$nextTick(() => {
				const el = this.$refs.tableScroll;
				if (el) el.scrollLeft = el.scrollWidth;
			});
		},
	},
	watch: {
		rows() { this.scrollToRight(); },
		isLoading(val) { if (!val) this.scrollToRight(); },
	},
};
</script>

<style scoped lang="scss">
.container_section {
	background: white;
	border-radius: 16px;
	border: 1px solid var(--report-border, #e5e7eb);
	box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
}

.tittle_section {
	font-size: 18px;
	font-weight: 600;
	color: var(--report-text, #111827);
}

.table_actions {
	gap: 8px;
}

.table_container {
	overflow-x: auto;
	border: 1px solid var(--report-border, #e5e7eb);
	border-radius: 12px;
	background: var(--report-bg);
	max-width: 100%;
}

.sticky-column {
	position: sticky;
	left: 0;
	z-index: 2;
	background-color: inherit;
	border-right: 1px solid var(--report-border, #e5e7eb);
}

.custom_table {
	min-width: 100%;
	border-collapse: separate;
	border-spacing: 0;

	td, th {
		height: 44px;
		padding: 12px 16px !important;
		border-bottom: 1px solid var(--report-divider-soft);
		vertical-align: middle;
	}

	thead th, td { text-align: end; }

	thead th {
		padding: 12px 24px;
		color: var(--report-text-muted);
		font-size: 12px;
		font-weight: 700;
		line-height: 18px;
		background-color: var(--report-surface);
	}

	tbody th {
		color: var(--report-text);
		font-size: 12px;
		font-weight: 500;
		line-height: 18px;
	}

	tbody td {
		color: var(--report-text-muted-strong);
		font-size: 14px;
		font-weight: 400;
		line-height: 20px;
	}

	.theme_row_white { background-color: var(--report-bg); }
	.theme_row_blue { background-color: var(--report-row-blue); }
	.theme_row_azure { background-color: var(--report-row-azure); }
	.theme_row_yellow { background-color: var(--report-row-yellow); }
	.theme_row_purple { background-color: var(--report-row-purple); }
	.theme_row_orange { background-color: var(--report-row-orange); }
	.theme_row_detail_bills {
		padding-left: 3em !important;
		background: var(--report-row-cream);
	}
}

.actual_button_arrow_down {
	width: 18px;
	height: 18px;
	vertical-align: middle;
	cursor: pointer;
}

.color_arrow {
	filter: brightness(0) saturate(100%) invert(20%) sepia(20%) saturate(500%) hue-rotate(180deg);
}

.rotate_arrow {
	transform: rotate(180deg);
}
</style>
